import type { BadgeSource, BrandConfig, NavKey, NavRequirement } from '@/brand/types';
import type { SidebarMenuItem } from '@/entities/sidebar/ui/MenuItem';
import type { ScreenId } from './types';
import { getScreen } from './screens';

// Данные пользователя, от которых зависит видимость и бейджи. Приходят из
// /me в рантайме — в конфиг они попасть не могут, конфиг лишь указывает,
// какое поле показывать (badge) и что проверять (requires).
//
// Флаги названы так же, как условия в NavRequirement: пункт с
// `requires: 'pwaHandoff'` читает ctx.pwaHandoff, и третье условие добавляется
// одной парой «ключ конфига — поле контекста», без правок isAllowed.
export type MenuContext = {
  referralSystem: boolean;
  pwaHandoff: boolean;
  badges: Record<BadgeSource, number | null>;
};

const isEnabled = (screens: Set<ScreenId>, id: ScreenId) => screens.has(id);

const isAllowed = (source: { requires?: NavRequirement }, ctx: MenuContext) =>
  source.requires ? ctx[source.requires] : true;

// Перевод подписей приходит параметром, а не берётся из активного словаря
// напрямую: подписи бренда — это оверрайды в его словаре, и тест обязан уметь
// проверить меню ОБОИХ брендов, а не только собранного (иначе проверка
// «у WapClick раздел называется Потоки» вырождается в тавтологию).
export type TranslateNav = (key: NavKey) => string;

// Приоритет подписи: свой ключ пункта → заголовок его экрана.
const label = (t: TranslateNav, source: { labelKey?: NavKey }, fallback: NavKey) =>
  t(source.labelKey ?? fallback);

// Меню — производное от реестра экранов и конфига бренда: ссылки и подписи по
// умолчанию берутся из реестра, бренд задаёт порядок, группировку, иконки и
// переопределения подписей («Основная» вместо «Статистика»).
export function buildMenu(
  config: BrandConfig,
  ctx: MenuContext,
  t: TranslateNav,
): SidebarMenuItem[] {
  const screens = new Set(config.screens);

  // Экраны, живущие табами, пункта меню не имеют (иначе к ним два пути), но
  // принадлежат разделу — их пути отдаём пункту как дополнительные признаки
  // активности, иначе на табе подсветка раздела гаснет.
  const tabSiblings = (id: ScreenId): string[] | undefined => {
    const group = config.tabGroups?.find((tab) => tab.screens.includes(id));
    if (!group) return undefined;

    const paths = group.screens
      .filter((screen) => screen !== id && isEnabled(screens, screen))
      .map((screen) => getScreen(screen).path);

    return paths.length > 0 ? paths : undefined;
  };

  return config.nav.reduce<SidebarMenuItem[]>((items, entry) => {
    if (!isAllowed(entry, ctx)) return items;

    if (entry.kind === 'link') {
      if (!isEnabled(screens, entry.screen)) return items;

      const screen = getScreen(entry.screen);
      items.push({
        iconName: entry.icon,
        label: label(t, entry, screen.titleKey),
        sublinks: [],
        link: screen.path,
        badge: entry.badge ? ctx.badges[entry.badge] : undefined,
        activePaths: tabSiblings(entry.screen),
        ...(entry.isNew ? { isNew: true } : {}),
      });

      return items;
    }

    // Рантайм-условие проверяется и у подпунктов, а не только у пунктов верхнего
    // уровня.
    const live = entry.items.filter(
      (item) => isEnabled(screens, item.screen) && isAllowed(item, ctx),
    );
    const sublinks = live.map((item) => {
      const screen = getScreen(item.screen);
      return {
        label: label(t, item, screen.titleKey),
        link: screen.path,
        ...(item.isNew ? { isNew: true } : {}),
      };
    });

    // Группа, у которой бренд выключил все экраны, не должна оставаться
    // пустым аккордеоном.
    if (sublinks.length === 0) return items;

    // Группа с единственным живым пунктом — уже не группа: аккордеон, который
    // раскрывается в одну строку, только мешает. Схлопываем в обычную ссылку,
    // подпись и иконка остаются от группы («Статистика» → /stat).
    if (sublinks.length === 1) {
      items.push({
        iconName: entry.icon,
        label: t(entry.labelKey),
        sublinks: [],
        link: sublinks[0].link,
        activePaths: tabSiblings(live[0].screen),
      });

      return items;
    }

    // Если сам primary выключен, клик по группе ведёт на первый живой пункт.
    const primary = isEnabled(screens, entry.primary)
      ? getScreen(entry.primary).path
      : sublinks[0].link;

    items.push({
      iconName: entry.icon,
      label: t(entry.labelKey),
      sublinks,
      link: primary,
    });

    return items;
  }, []);
}
